import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import Button from "@/components/ui/button";
import Input from "@/components/ui/input";
import Switch from "@/components/ui/switch";
import Textarea from "@/components/ui/textarea";
import Tooltip from "@/components/ui/tooltip";
import LoadingScreen from "@/components/ui/loading";
import { ArrowLeft as ArrowLeftIcon, Plus as PlusIcon } from "@/components/icons";

import { rolesAndPermissionsApi } from "@/features/roles-and-permissions/services/roles-and-permissions.api";
import { staffManagementApi } from "@/features/staff-management/services/staff-management.api";

import ApprovalStepCard, { type AssigneeOption } from "../components/ApprovalStepCard";
import { approvalFlowsApi } from "../services/approval-flows.api";
import { ApprovalTargetType } from "../types/approval-flows.types";
import {
  emptyStep,
  stepsKey,
  toBuilderSteps,
  toStepPayload,
  validateSteps,
  type BuilderBranch,
  type BuilderStep,
} from "../types/flow-builder.types";
import styles from "./ApprovalFlowBuilderPage.module.scss";

/**
 * Akışın hedef türü ekranda sorulmaz, sabittir.
 *
 * Motorda kayıtlı tek tüketici eğitim kaydı (bkz. backend
 * `EducationEnrollmentApprovalService`); diğer hedef türlerinde akış hiçbir
 * zaman tetiklenmez. Seçim kullanıcıya bırakıldığında, türü yanlış seçilen akış
 * sessizce ölü kalıyor ve kursiyer talep açamıyordu.
 */
const FLOW_TARGET_TYPE = ApprovalTargetType.EDUCATION_ENROLLMENT;

/**
 * Onay akışı oluşturma / düzenleme ekranı.
 *
 * Adım sıraları sunucuya gönderilmez — backend, adımları dizideki sıraya göre
 * 1..N olarak numaralandırır. Bu yüzden ekranda tek doğru kaynak `steps`
 * dizisinin kendi sırasıdır; taşıma ve araya ekleme sadece diziyi düzenler.
 *
 * Düzenleme modunda kaydetme tek bir PATCH ile yapılır (adım tasarımı değiştiyse
 * `steps` alanı gönderilir, backend adım listesini tamamen yeniden yazar). Adım
 * adım insert/reorder uçlarını kullanmak yerine bunu tercih ettik: kullanıcı
 * "Kaydet"e basana kadar hiçbir şey kalıcı olmuyor, yarım kalmış düzenleme akışı
 * bozmuyor.
 *
 * Form modeli `BuilderStep`tir: her kademe en az bir DAL taşır. Sunucu adımları
 * düz gönderdiği (paralel kademe = aynı stepOrder'lı birden fazla satır) ve
 * gönderim biçimi iki türlü olduğu için dönüştürme tek yerde toplandı
 * (bkz. flow-builder.types).
 */
const ApprovalFlowBuilderPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [steps, setSteps] = useState<BuilderStep[]>([emptyStep()]);
  /** Düzenlemede yüklenen tasarımın anahtarı; kaydetmede değişiklik kontrolü için. */
  const [initialStepsKey, setInitialStepsKey] = useState<string | null>(null);

  const [roleOptions, setRoleOptions] = useState<AssigneeOption[]>([]);
  const [userOptions, setUserOptions] = useState<AssigneeOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  /** Rol listesi — adım onaycısı ROL seçildiğinde kullanılır. */
  useEffect(() => {
    let cancelled = false;
    rolesAndPermissionsApi
      .getAll({ page: 1, pageSize: 100 })
      .then((res) => {
        if (cancelled) return;
        setRoleOptions(res.data.map((r) => ({ value: r.id, label: r.roleName })));
      })
      .catch(() => {
        if (!cancelled) setRoleOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /** Personel listesi — sunucu taraflı arama ile beslenir. */
  const loadUsers = useCallback((search?: string) => {
    staffManagementApi
      .getAll({ page: 1, pageSize: 20, filters: search ? { search } : undefined })
      .then((res) => {
        setUserOptions(
          res.data.map((s) => ({
            value: s.id,
            label: s.title ? `${s.name} — ${s.title}` : s.name,
          })),
        );
      })
      .catch(() => setUserOptions([]));
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  /** Düzenleme modunda mevcut akışı forma yükler. */
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    approvalFlowsApi
      .getById(id)
      .then((flow) => {
        if (cancelled) return;
        setTitle(flow.title);
        setDescription(flow.description ?? "");
        setIsActive(flow.isActive);
        const loadedSteps = toBuilderSteps(flow.steps);
        setSteps(loadedSteps);
        setInitialStepsKey(stepsKey(loadedSteps));
        // Kayıtlı kişi atamaları arama listesinde olmayabilir; etiketleri
        // kaybolmasın diye seçenek listesine ekliyoruz.
        const assigned = flow.steps.flatMap((s) =>
          s.user ? [{ value: s.user.id, label: s.user.name }] : [],
        );
        if (assigned.length > 0) {
          setUserOptions((prev) => {
            const seen = new Set(prev.map((o) => o.value));
            return [...prev, ...assigned.filter((o) => !seen.has(o.value))];
          });
        }
      })
      .catch(() => {
        toast.error("Akış yüklenemedi");
        navigate("/dashboard/approval-flows");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  const handleStepChange = useCallback((index: number, patch: Partial<BuilderStep>) => {
    setSteps((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  }, []);

  /** Kademenin tek bir dalını günceller; diğer dallar olduğu gibi kalır. */
  const handleBranchChange = useCallback(
    (index: number, branchIndex: number, patch: Partial<BuilderBranch>) => {
      setSteps((prev) =>
        prev.map((step, i) =>
          i === index
            ? {
                ...step,
                branches: step.branches.map((branch, j) =>
                  j === branchIndex ? { ...branch, ...patch } : branch,
                ),
              }
            : step,
        ),
      );
    },
    [],
  );

  const handleRemove = useCallback((index: number) => {
    setSteps((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));
  }, []);

  const handleMove = useCallback((index: number, direction: -1 | 1) => {
    setSteps((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }, []);

  /** Seçilen adımın öncesine/sonrasına boş bir adım açar. */
  const handleInsert = useCallback((index: number, position: "BEFORE" | "AFTER") => {
    setSteps((prev) => {
      const at = position === "BEFORE" ? index : index + 1;
      const next = [...prev];
      next.splice(at, 0, emptyStep());
      return next;
    });
  }, []);

  const handleAppend = useCallback(() => {
    setSteps((prev) => [...prev, emptyStep()]);
  }, []);

  /** Kaydetmeden önce zorunlu alan kontrolü — backend de doğruluyor, bu erken geri bildirim. */
  const validationError = useMemo(() => {
    if (!title.trim()) return "Akış grubu adı zorunludur.";
    return validateSteps(steps);
  }, [title, steps]);

  const handleSave = async () => {
    setShowErrors(true);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    const payloadSteps = steps.map(toStepPayload);

    // Adım tasarımı dokunulmadıysa `steps` gönderilmiyor: karar bekleyen talebi
    // olan akış da böylece pasife alınabiliyor, adım satırları da boşuna silinip
    // yeniden yazılmıyor.
    const stepsChanged = initialStepsKey === null || stepsKey(steps) !== initialStepsKey;

    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim() || undefined,
        targetType: FLOW_TARGET_TYPE,
        isActive,
      };
      if (isEdit && id) {
        await approvalFlowsApi.update(id, {
          ...payload,
          ...(stepsChanged ? { steps: payloadSteps } : {}),
        });
        toast.success("Akış güncellendi");
      } else {
        await approvalFlowsApi.create({ ...payload, steps: payloadSteps });
        toast.success("Akış oluşturuldu");
      }
      navigate("/dashboard/approval-flows");
    } catch {
      // Hata mesajını apiClient global toast'u gösteriyor (başlık çakışması,
      // "akışın talebi var" gibi 409'lar burada kullanıcıya ulaşır).
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <Header title={isEdit ? "Akışı Düzenle" : "Onay Akışı Ekle"} />
        <LoadingScreen />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Header title={isEdit ? "Akışı Düzenle" : "Onay Akışı Ekle"} />

      <div className={styles.topBar}>
        <div className={styles.topBarTitle}>
          <button
            className={styles.backButton}
            onClick={() => navigate("/dashboard/approval-flows")}
            type="button"
          >
            <ArrowLeftIcon height={18} width={18} />
          </button>
          <div>
            <h2>{isEdit ? "Akışı Düzenle" : "Onay Akışı Ekle"}</h2>
            <p>
              Eğitim talepleri bu akıştan geçer. Adımları sırayla ekleyin; her adımı bir role ya da
              kişiye atayın. Aynı kademede birden fazla onaycı olacaksa adıma paralel dal ekleyin.
            </p>
          </div>
        </div>
        <div className={styles.topBarActions}>
          <Button
            color="default"
            onClick={() => navigate("/dashboard/approval-flows")}
            variant="outlined"
          >
            İptal
          </Button>
          <Button color="primary" loading={saving} onClick={handleSave} variant="contained">
            Kaydet
          </Button>
        </div>
      </div>

      <section className={styles.groupCard}>
        <span className={styles.groupLabel}>GRUP BİLGİLERİ</span>
        <div className={styles.groupFields}>
          <Input
            error={showErrors && !title.trim()}
            label="Akış Grubu Adı"
            maxLength={200}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Örn. Eğitim Talep Onay Akışı"
            required
            value={title}
          />
          <Textarea
            label="Açıklama"
            maxLength={2000}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Bu akışın ne için kullanıldığını yazın"
            rows={2}
            value={description}
          />
          <div className={styles.groupMeta}>
            <div className={styles.activeToggle}>
              <Switch checked={isActive} onChange={setIsActive} variant="primary" />
              <span className={styles.metaLabel}>
                {isActive ? "Aktif" : "Pasif — yeni talep başlatılamaz"}
              </span>
            </div>
          </div>
        </div>
      </section>

      <div className={styles.canvas}>
        <div className={styles.canvasHeader}>
          <span>ADIMLAR</span>
          <span className={styles.stepCount}>{steps.length} adım</span>
        </div>

        <div className={styles.stepList}>
          {steps.map((step, index) => (
            <ApprovalStepCard
              index={index}
              key={index}
              onBranchChange={handleBranchChange}
              onChange={handleStepChange}
              onInsert={handleInsert}
              onMove={handleMove}
              onRemove={handleRemove}
              onUserSearch={loadUsers}
              roleOptions={roleOptions}
              showErrors={showErrors}
              step={step}
              total={steps.length}
              userOptions={userOptions}
            />
          ))}
        </div>

        <div className={styles.appendRow}>
          <Tooltip content="Akışın sonuna yeni adım ekle">
            <Button
              color="default"
              onClick={handleAppend}
              startIcon={<PlusIcon color="currentColor" height={16} width={16} />}
              variant="outlined"
            >
              Adım Ekle
            </Button>
          </Tooltip>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ApprovalFlowBuilderPage;
